import {
  getOpenAIVideoModelProfile,
  VIDEO_PLATFORM_SPECS,
  type JobStatus,
  type PublicJob,
} from "@farzin/contracts";

const STATUS_LABELS: Readonly<Record<JobStatus, string>> = {
  queued: "Queued",
  generating: "Rendering",
  downloading: "Finishing",
  completed: "Delivered",
  failed: "Needs retry",
};

export interface JobListProps {
  readonly jobs: readonly PublicJob[];
  readonly loading: boolean;
}

export function JobList({ jobs, loading }: JobListProps): React.JSX.Element {
  return (
    <section className="jobs-section" aria-labelledby="recent-heading">
      <div className="jobs-title-row">
        <div>
          <span className="eyebrow">YOUR ARCHIVE</span>
          <h2 id="recent-heading">Recent films</h2>
        </div>
        <span className="job-count">
          {jobs.length.toString().padStart(2, "0")}
        </span>
      </div>
      {loading ? (
        <div className="empty-state pixel-border">Loading your studio…</div>
      ) : null}
      {!loading && jobs.length === 0 ? (
        <div className="empty-state pixel-border">
          <span>▦</span>
          <strong>Your first film begins above.</strong>
          <p>Describe a small moment and bring it to life.</p>
        </div>
      ) : null}
      <div className="job-list">
        {jobs.map((job) => (
          <JobCard job={job} key={job.id} />
        ))}
      </div>
    </section>
  );
}

function JobCard({ job }: { readonly job: PublicJob }): React.JSX.Element {
  const active =
    job.status === "queued" ||
    job.status === "generating" ||
    job.status === "downloading";
  return (
    <article className="job-card pixel-border">
      <div className={`job-thumbnail ${job.stylePreset}`}>
        <span>{active ? "▶" : job.status === "completed" ? "✓" : "!"}</span>
      </div>
      <div className="job-content">
        <div className="job-meta">
          <span className={`status ${job.status}`}>
            {STATUS_LABELS[job.status]}
          </span>
          <time>{relativeTime(job.createdAt)}</time>
        </div>
        <p>{job.story}</p>
        <div className="job-footer">
          <span>
            {getOpenAIVideoModelProfile(job.videoModel).label} ·{" "}
            {VIDEO_PLATFORM_SPECS[job.platform].label} · {String(job.frameRate)}{" "}
            fps
          </span>
          <code>{job.id.slice(0, 8)}</code>
        </div>
        {active ? (
          <div className="progress-track">
            <span style={{ width: `${String(job.progress)}%` }} />
          </div>
        ) : null}
        {job.errorMessage ? (
          <small className="job-error">{job.errorMessage}</small>
        ) : null}
      </div>
    </article>
  );
}

function relativeTime(iso: string): string {
  const minutes = Math.max(
    0,
    Math.round((Date.now() - new Date(iso).getTime()) / 60_000),
  );
  if (minutes < 1) return "now";
  if (minutes < 60) return `${String(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  return hours < 24
    ? `${String(hours)}h`
    : `${String(Math.floor(hours / 24))}d`;
}
